"use client";

import {
	Avatar,
	type DefaultImageProps,
	type DefaultLinkProps,
	FeaturedText,
	Icon,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	RichText,
	Text
} from "@pamperedchef/design-system";
import { className } from "@/app/utilities/className";
import type { SeasonTheme } from "@/app/utilities/types";

import * as styles from "./HeroBanner.module.scss";

export interface HeroBannerProps<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
> extends React.HTMLAttributes<HTMLDivElement> {
	className?: string;
	theme?: SeasonTheme;
	headline: string;
	description?: string;
	coverImage: ImageProps<ImageComponentProps>;
	consultant?: {
		name?: string;
		description?: string;
		avatarImage?: ImageProps<ImageComponentProps>;
		defaultImage?: ImageProps<ImageComponentProps>;
	};
	primaryCta?: LinkProps<LinkComponentProps>;
	secondaryCta?: LinkProps<LinkComponentProps>;
	consultantCta?: LinkProps<LinkComponentProps>;
}

/**
 * Hero Banner component
 */
export function HeroBanner<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
>({
	theme = "standard",
	headline,
	description,
	coverImage,
	consultant,
	primaryCta,
	secondaryCta,
	consultantCta,
	...props
}: HeroBannerProps<LinkComponentProps, ImageComponentProps>): JSX.Element {
	return (
		<div
			{...className(
				styles["hero-banner"],
				theme && styles[`hero-banner--${theme}`],
				props.className
			)}
		>
			<div className={`grid ${styles["hero-banner__container"]}`}>
				<div className="col-xs-12 col-lg-5 col-xxl-4">
					<div className={styles["hero-banner__left-section"]}>
						<div className={styles["hero-banner__content"]}>
							<FeaturedText
								alignment="left"
								headline={headline}
								theme="star"
								styledAs="display-sm"
							/>

							<RichText
								className={styles["hero-banner__content__text"]}
								content={description || ""}
							/>

							<div className={styles["hero-banner__actions"]}>
								{primaryCta?.href && (
									<Link<LinkComponentProps>
										{...primaryCta}
										kind="primary"
									/>
								)}
								{secondaryCta?.href && (
									<Link<LinkComponentProps>
										{...secondaryCta}
										kind="secondary"
									/>
								)}
							</div>
						</div>

						<div className={styles["hero-banner__contact"]}>
							{consultant?.avatarImage && (
								<div
									className={
										styles["hero-banner__contact--avatar"]
									}
								>
									<Avatar
										size="large"
										{...consultant.avatarImage}
									/>
								</div>
							)}

							{consultant?.defaultImage &&
								!consultant?.avatarImage && (
									<div
										className={
											styles[
												"hero-banner__contact--image"
											]
										}
									>
										{/* @ts-expect-error - React 19 generic type limitation with Image component */}
										<Image<ImageComponentProps>
											{...consultant.defaultImage}
											aspectRatio="16:9"
											objectFit="cover"
											alt={
												consultant.defaultImage.alt ||
												""
											}
										/>
									</div>
								)}

							<div>
								{consultant?.name && (
									<Text styledAs="smb">
										{consultant?.name}
									</Text>
								)}

								{consultant?.description && (
									<RichText
										content={consultant?.description}
										className={
											styles[
												"hero-banner__contact__description"
											]
										}
									/>
								)}

								{consultantCta?.href && (
									<Link<LinkComponentProps>
										{...consultantCta}
										icon={{ name: "ChevronRight" }}
										kind="tertiary"
									/>
								)}
							</div>
						</div>

						<Icon
							name="hero-background-standard"
							className={styles["hero-banner__background"]}
						/>
					</div>
				</div>
				<div className="col-xs-12 col-lg-7 col-xxl-8">
					<div className={styles["hero-banner__hero"]}>
						{/* @ts-expect-error - React 19 generic type limitation with Image component */}
						<Image<ImageComponentProps>
							{...coverImage}
							aspectRatio="16:9"
							objectFit="cover"
							className={styles["hero-banner__hero-image"]}
							alt={coverImage.alt || ""}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
